import { FC, useState, Fragment, useEffect } from 'react';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import {
  AppBar,
  Toolbar,
  FormGroup,
  Select as _Select,
  Typography,
  Tabs,
  Tab,
  TextField as _TextField,
} from '@material-ui/core';
import theme from '../../../../theme';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { SPACING_UNIT, SPACING_REGULAR } from '../../../constant/spacing';
import { DatePicker } from '@material-ui/pickers';
import GlobalPeriod from '../../../../entity/GlobalPeriod';
import CustomReport from '../../../../entity/CustomReport';
import ChartGroup from '../../../../entity/ChartGroup';
import Spinner from '../../primitive/Spinner';

type Props = {
  onSelectFromPeriod: (date: DateTime | null) => void;
  onSelectToPeriod: (date: DateTime | null) => void;
  customReportChartPeriod: GlobalPeriod;
  customReport?: CustomReport | null;
  customReportLoading: boolean;
  selectedChartGroup: number;
  setSelectedChartGroup: (data: number) => void;
  className?: string;
};

const CustomReportToolBar: FC<Props> = ({
  onSelectFromPeriod,
  onSelectToPeriod,
  customReportChartPeriod,
  customReport,
  customReportLoading,
  selectedChartGroup,
  setSelectedChartGroup,
  className,
}) => {
  const [from, setFrom] = useState<DateTime | null>(customReportChartPeriod.from);
  const [to, setTo] = useState<DateTime | null>(customReportChartPeriod.to);
  const [chartGroup, setChartGroup] = useState<ChartGroup[] | null>();
  const [tabValue, setTabValue] = useState<number>(selectedChartGroup);

  if (from !== customReportChartPeriod.from) {
    setFrom(customReportChartPeriod.from);
  }

  if (to !== customReportChartPeriod.to) {
    setTo(customReportChartPeriod.to);
  }

  const handleTabChange = (_: React.ChangeEvent<Record<string, never>>, newValue: number) => {
    setTabValue(newValue);
    setSelectedChartGroup(newValue);
  };

  const handleFromChange = (date: DateTime | null) => {
    setFrom(date);
    date ? onSelectFromPeriod(date) : null;
  };

  const handleToChange = (date: DateTime | null) => {
    setTo(date);
    date ? onSelectToPeriod(date) : null;
  };

  useEffect(() => {
    if (customReport) {
      setChartGroup(customReport.groups);
      // FIXME: なせかselectedChartGroupがStringでくるので修正. 一旦以下で対応
      setTabValue(Number(selectedChartGroup));
    } else {
      setChartGroup(null);
    }
  });

  return (
    <Root position="static" className={className}>
      <Toolbar>
        <Form row>
          {customReportLoading ? (
            <PeriodSpinner size={24} width={'auto'} height={64} />
          ) : (
            <Tabs value={tabValue} indicatorColor="primary" textColor="primary" onChange={handleTabChange}>
              {chartGroup
                ? chartGroup.map((item) => {
                    return <GroupTab label={item.name} key={item.name} />;
                  })
                : null}
            </Tabs>
          )}
          <Period>
            <Fragment>
              <Typography variant="h6">期間</Typography>
              <PeriodContainer>
                <DatePicker
                  inputFormat="yyyy/MM/dd"
                  value={from}
                  //@ts-ignore
                  onChange={handleFromChange}
                  OpenPickerButtonProps={{
                    'aria-label': 'change date',
                  }}
                  maxDate={to}
                  renderInput={(props) => <TextField {...props} />}
                />
                <Typography>~</Typography>
                <DatePicker
                  inputFormat="yyyy/MM/dd"
                  value={to}
                  //@ts-ignore
                  onChange={handleToChange}
                  OpenPickerButtonProps={{
                    'aria-label': 'change date',
                  }}
                  minDate={from}
                  renderInput={(props) => <TextField {...props} />}
                />
              </PeriodContainer>
            </Fragment>
          </Period>
        </Form>
      </Toolbar>
    </Root>
  );
};

const Root = styled(AppBar)({
  paddingLeft: SPACING_UNIT,
  paddingRight: 42,
  backgroundColor: theme.palette.common.white,
  borderBottom: `1px solid ${theme.palette.grey['200']}`,
  boxShadow: 'none',
});

const Period = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
  { height: 64, marginRight: '240px' },
);

const Form = styled(FormGroup)(
  flexibleLayoutContainer({
    justifyContent: 'space-between',
  }),
  {
    width: '100%',
  },
);

const PeriodContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const TextField = styled(_TextField)({
  margin: `0px ${SPACING_REGULAR}px`,
});

const GroupTab = styled(Tab)({
  height: '64px',
  textTransform: 'none',
});

const PeriodSpinner = styled(Spinner)({
  marginLeft: SPACING_REGULAR,
  marginRight: SPACING_REGULAR,
  color: theme.palette.common.white,
});

export default CustomReportToolBar;
